import React, { useEffect, useState } from 'react'
import * as yup from 'yup'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import { useIntl } from 'react-intl'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogTitle from '@mui/material/DialogTitle'
import {
    Box,
    CircularProgress,
    DialogContent,
    IconButton,
    useTheme,
} from '@mui/material'
import EditIcon from '../../../../../assets/icons/Edit/Edit.svg'
import {
    requiredId,
    requiredString,
    isRequiredDate,
} from '../../../../../utils/validation'
import { H6, H3 } from '../../../../../layouts/TextStyles'
import CustomInput from '../../../../../components/muiComponents/customInput'
import { CourseExamState } from '../../../../../features/types'
import { courseExamApiSlice } from '../../../../../features/course/courseExamSlice'
import DateTimePicker from '../../../../../components/muiComponents/timePicker/TimePicker'
import FlagKz from '../../../../../assets/img/flag-kz.png'
import FlagRu from '../../../../../assets/img/flag-ru.png'
import dayjs from 'dayjs'
import { Moment } from 'moment'


interface Props {
    editCourseExam?: CourseExamState | null
    editMode?: boolean
    courseId?: number
}

const AddExamDialog: React.FC<Props> = ({
    editMode = false,
    editCourseExam = null,
    courseId,
}) => {
    const { formatMessage, locale } = useIntl()
    const theme = useTheme()


    const styles = {
        flag: {
            width: 24,
            height: 24,
            marginLeft: 10,
            marginTop: 6,
        },
        titleBox: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
        },
        boxHeight: {
            height: '100px',
        },
    }



    const [open, setOpen] = useState(false)



    const [createCourseExam, { isLoading: isCreating }] =
        courseExamApiSlice.useCreateCourseExamMutation()

    const [updateCourseExam, { isLoading: isUpdating }] =
        courseExamApiSlice.useUpdateCourseExamMutation()

    const validationSchema = yup.object().shape({
        courseId: requiredId({ formatMessage }),
        titleKz: requiredString({ formatMessage }),
        titleRu: requiredString({ formatMessage }),
        edutestExamCode: requiredString({ formatMessage }),
        startDateTime: isRequiredDate({ formatMessage }),
        endDateTime: isRequiredDate({ formatMessage }),
    })

    const courseExam: CourseExamState = {
        id: editCourseExam?.id,
        courseId: editCourseExam?.courseId ?? courseId,
        titleKz: editCourseExam?.titleKz ?? '',
        titleRu: editCourseExam?.titleRu ?? '',
        edutestExamCode: editCourseExam?.edutestExamCode ?? '',
        startDateTime: editCourseExam?.startDateTime ?? null,
        endDateTime: editCourseExam?.endDateTime ?? null,
    }

    const formik = useFormik({
        initialValues: courseExam,
        validationSchema: validationSchema,
        validateOnChange: false,
        validateOnBlur: false,

        onSubmit: async () => {
            if (editCourseExam) {
                handleUpdateCourseExam()
            } else {
                handleCreateCourseExam()
            }
        },
    })




    // ...
    const handleDateChange = (key: 'startDateTime' | 'endDateTime', event: Moment) => {
        formik.setFieldValue(
            key,
            dayjs(event.toDate()).format('YYYY-MM-DDTHH:mm:ss'),
        )
    }

    const handleCreateCourseExam = async () => {
        try {
            const { courseId, titleKz, titleRu, edutestExamCode, startDateTime, endDateTime } = formik.values as CourseExamState
            await createCourseExam({
                courseId,
                body: { titleKz, titleRu, edutestExamCode, startDateTime, endDateTime },
            }).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            handleClose()
        } catch (error) { }
    }

    const handleUpdateCourseExam = async () => {
        try {
            const { id, titleKz, titleRu, edutestExamCode, startDateTime, endDateTime } = formik.values as CourseExamState
            await updateCourseExam({
                id,
                body: { titleKz, titleRu, edutestExamCode, startDateTime, endDateTime },
            }).unwrap()
            toast.success(formatMessage({ id: 'updatedSuccess' }))
            handleClose()
        } catch (error) { }
    }


    const handleClickOpen = () => {
        setOpen(true)
        formik.setValues(courseExam)
    }

    const handleClose = () => {
        setOpen(false)
    }



    return (
        <div>
            {editMode ? (
                <IconButton
                    sx={{ background: '#f3f4f5' }}
                    onClick={handleClickOpen}
                >
                    <img
                        src={EditIcon}
                        alt="edit"
                    />
                </IconButton>
            ) : (
                <Button
                    variant={'contained'}
                    onClick={handleClickOpen}
                >
                    {formatMessage({ id: 'addExam' })}
                </Button>
            )}

            <Dialog
                maxWidth={'lg'}
                open={open}
                onClose={handleClose}
                fullWidth
            >
                <DialogTitle id="responsive-dialog-title">
                    {editMode
                        ? formatMessage({ id: 'editData' })
                        : formatMessage({ id: 'addExam' })}
                </DialogTitle>

                <DialogContent>
                    <form onSubmit={formik.handleSubmit}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Box sx={{ width: '100%' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                    <Box sx={{ height: '160px', width: '100%' }}>
                                        <Box sx={styles.titleBox}>
                                            <H3>Емтихан аты</H3>

                                            <img
                                                alt="flag"
                                                src={FlagKz}
                                                style={styles.flag}
                                            />
                                        </Box>

                                        <CustomInput
                                            id="titleKz"
                                            name="titleKz"
                                            value={formik.values.titleKz}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            error={Boolean(formik.errors.titleKz)}
                                            helperText={formik.errors.titleKz}
                                            placeholder="Enter name"
                                            fullWidth
                                        />
                                    </Box>
                                    <Box sx={{ height: '160px', width: '100%' }}>
                                        <Box sx={styles.titleBox}>
                                            <H3>Название экзамена</H3>

                                            <img
                                                alt="flag"
                                                src={FlagRu}
                                                style={styles.flag}
                                            />
                                        </Box>

                                        <CustomInput
                                            id="titleRu"
                                            name="titleRu"
                                            value={formik.values.titleRu}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            error={Boolean(formik.errors.titleRu)}
                                            helperText={formik.errors.titleRu}
                                            placeholder="Enter name"
                                            fullWidth
                                        />
                                    </Box>
                                </Box>

                                <Box sx={{ height: '120px' }} mb={2}>
                                    <H6>{formatMessage({ id: 'start&endTime' })}</H6>
                                    <Box sx={{ display: 'flex', mt: 2 }}>
                                        <Box sx={{ mr: 2 }}>

                                            <DateTimePicker
                                                label={formatMessage({ id: 'startDateTime' })}
                                                value={formik.values.startDateTime}
                                                handleChange={(event) =>
                                                    handleDateChange('startDateTime', event)
                                                }
                                            />
                                        </Box>
                                        <Box>
                                            <DateTimePicker
                                                label={formatMessage({ id: 'endDateTime' })}
                                                value={formik.values.endDateTime}
                                                handleChange={(event) =>
                                                    handleDateChange('endDateTime', event)
                                                }
                                            />
                                        </Box>
                                    </Box>
                                </Box>


                                <Box sx={{ height: '120px' }}>
                                    <H6>{formatMessage({ id: 'edutestExamCode' })}</H6>
                                    <CustomInput
                                        id="edutestExamCode"
                                        name="edutestExamCode"
                                        type="text"
                                        placeholder={formatMessage({ id: 'edutestExamCode' })}
                                        sx={{ marginTop: '8px' }}
                                        fullWidth
                                        value={formik.values.edutestExamCode}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={Boolean(formik.errors.edutestExamCode)}
                                        helperText={formik.errors.edutestExamCode as any}
                                        margin="dense"
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </form>
                </DialogContent>

                <DialogActions sx={{ m: 2, mt: 0 }}>
                    <Button
                        onClick={handleClose}
                        variant={'outlined'}
                        sx={{ mr: 1 }}
                        disabled={isCreating || isUpdating}
                    >
                        {formatMessage({ id: 'cancel' })}
                    </Button>

                    <Button
                        variant={'contained'}
                        sx={{ width: '120px' }}
                        disabled={isCreating || isUpdating}
                        onClick={() => formik.handleSubmit()}
                    >
                        {isCreating || isUpdating ? (
                            <CircularProgress
                                size={20}
                                style={{ marginRight: '8px' }}
                            />
                        ) : (
                            formatMessage({ id: 'save' })
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

export default AddExamDialog
